import { isNil } from '@src/shared';
import settings from '@src/shared/config';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { readStoredCredentials } from '../lib/credentialsStorage';
import { AUTH_STORAGE_KEY } from '../lib/constants';
import { saveReturnUrl } from '../lib/returnUrl';
import type { JwtCredentials } from './providers';

function useJwtStorage() {
  const parse = useCallback(() => readStoredCredentials(), []);

  const [credentials, setCredentials] = useState(parse);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (
        event.storageArea === localStorage &&
        (event.key === AUTH_STORAGE_KEY || event.key === null)
      ) {
        const nextCredentials = parse();
        const currentUserId = credentials?.user?.id ?? null;
        const nextUserId = nextCredentials?.user?.id ?? null;
        const authenticationChanged =
          Boolean(credentials) !== Boolean(nextCredentials);
        const identityChanged =
          currentUserId !== nextUserId ||
          credentials?.loginAs !== nextCredentials?.loginAs;

        if (authenticationChanged || identityChanged) {
          if (credentials && !nextCredentials) {
            saveReturnUrl(
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
              currentUserId,
            );
          }
          window.location.reload();
          return;
        }

        setCredentials(nextCredentials);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [credentials, parse]);

  const isAccessTokenExpired = useMemo(() => {
    const expiredAt = credentials?.metadata?.accessTokenExpiredAt;
    if (isNil(expiredAt)) {
      return true;
    }
    const delta = expiredAt - Date.now();
    return delta < settings.AUTH.JWT.EXPIRED_BUFFER;
  }, [credentials]);

  const isRefreshTokenExpired = useMemo(() => {
    const expiredAt = credentials?.metadata?.refreshTokenExpiredAt;
    if (isNil(expiredAt)) {
      return true;
    }
    const delta = expiredAt - Date.now();
    return delta < settings.AUTH.JWT.EXPIRED_BUFFER;
  }, [credentials]);

  const isExpired = useMemo(
    () => isAccessTokenExpired || isRefreshTokenExpired,
    [isAccessTokenExpired, isRefreshTokenExpired],
  );
  const isAuthenticated = useMemo(
    () => !isExpired && Boolean(credentials),
    [credentials, isExpired],
  );

  const set = useCallback((payload: JwtCredentials) => {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
    setCredentials(payload);
  }, []);

  const remove = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setCredentials(null);
  }, []);

  return {
    // JwtAuthProvider retains this adapter for its entire lifetime, so these
    // methods must remain referentially stable.
    set,
    remove,
    parse,
    credentials,
    isAuthenticated,
  };
}

export { AUTH_STORAGE_KEY, useJwtStorage };
